import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'crossroad';
import { calculateBiblePositionPercent, type BibleBook } from '@/lib/utils/bible-utils';
import { parseRef } from '@/lib/utils/reference-utils';
import { usePanelSettled } from '@/context/PanelSettledContext';

interface TransitionState {
    isAnimating: boolean;
    sourceRect: DOMRect | null;
    targetRect: DOMRect | null;
    targetPercent: number;
}

interface UseContextTransitionOptions {
    passageBook: string;
    passageRef: string;
    mode: 'book' | 'bible';
}

/**
 * Hook to manage the "zoom to canon bar" transition animation.
 * 
 * Captures the passage text position, calculates the target position on the
 * canon bar (exact arrow position), and provides state for animating a ghost element.
 */
export function useContextTransition({ passageBook, passageRef, mode }: UseContextTransitionOptions) {
    const contextCategory = useParams('/app/passage/:id/context/:contextCategory')?.contextCategory;
    const [state, setState] = useState<TransitionState>({
        isAnimating: false,
        sourceRect: null,
        targetRect: null,
        targetPercent: 0,
    });

    const prefersReducedMotion = useRef(false);
    const previousContextRef = useRef<string | undefined>(undefined);
    const hasTriggeredRef = useRef(false);

    // Check reduced motion preference
    useEffect(() => {
        if (typeof window !== 'undefined') {
            prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        }
    }, []);

    const { waitForSettled } = usePanelSettled();

    // Trigger animation when entering scripture or book context
    useEffect(() => {
        const isEnteringRelevantContext =
            (contextCategory === 'scripture' || contextCategory === 'book' || contextCategory === 'historicalEvents') &&
            previousContextRef.current !== contextCategory;

        previousContextRef.current = contextCategory;

        if (!isEnteringRelevantContext || prefersReducedMotion.current || hasTriggeredRef.current) {
            return;
        }

        // Mark as triggered to prevent re-triggering
        hasTriggeredRef.current = true;

        // 1. Capture source position (passage text) IMMEDIATELY before panel starts covering it
        const passageContainer = document.querySelector('[data-passage-text-container]');
        if (!passageContainer) {
            return;
        }
        const sourceRect = passageContainer.getBoundingClientRect();

        // 2. Wait for panel animation to complete using the signal-based approach
        let cancelled = false;

        const startAnimation = async () => {
            // Wait for the panel to settle (animation complete)
            await waitForSettled();

            if (cancelled) return;

            // Extra frame to ensure layout is painted
            await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

            if (cancelled) return;

            // Try to find the specific target for the context
            const targetId = contextCategory === 'historicalEvents'
                ? 'passage-timeline-target'
                : 'horizontal-context-navigator';

            const targetEl = document.getElementById(targetId);

            if (targetEl) {
                const targetRect = targetEl.getBoundingClientRect();

                // Read the dynamic target position (0-100) from the element's data attribute
                // For timeline, we default to center of the element (50)
                const dynamicTargetPercent = parseFloat(targetEl.getAttribute('data-target-position') || '50');

                setState({
                    isAnimating: true,
                    sourceRect,
                    targetRect,
                    targetPercent: dynamicTargetPercent,
                });
            }
        };

        startAnimation();

        return () => {
            cancelled = true;
        };
    }, [contextCategory, waitForSettled]);

    // Reset trigger flag when leaving context pages
    useEffect(() => {
        if (!contextCategory) {
            hasTriggeredRef.current = false;
        }
    }, [contextCategory]);

    const endAnimation = useCallback(() => {
        setState(prev => ({ ...prev, isAnimating: false }));
    }, []);

    return {
        ...state,
        endAnimation,
        prefersReducedMotion: prefersReducedMotion.current,
    };
}
